// AuditLogPage — Brokerage Desk -> Audit Log (managing broker / office manager): every
// successful change made by a signed-in person — who, what (method, screen area, record),
// when, from where. Append-only. Search / sort / CSV of what is shown, or the full CSV
// for a date range from the server.
import React, { useCallback, useEffect, useState } from 'react';
import { useApiClient } from '../../hooks/useApiClient';
import DataList, { downloadCsv } from '../../components/shared/DataList';

const VERB = { POST: 'created / did', PUT: 'changed', PATCH: 'changed', DELETE: 'removed' };

export default function AuditLogPage() {
    const api = useApiClient();
    const [rows, setRows] = useState([]);
    const [range, setRange] = useState({ from: '', to: '' });
    const [err, setErr] = useState('');
    const qs = () => new URLSearchParams(Object.entries(range).filter(([, v]) => v)).toString();
    const load = useCallback(async () => {
        try { setRows(await api.get(`/api/desk/audit-log?${new URLSearchParams(Object.entries(range).filter(([, v]) => v)).toString()}`)); setErr(''); } catch (e) { setErr(e.message); }
    }, [api, range]);
    useEffect(() => { load(); }, [load]);
    const fullCsv = async () => {
        try {
            const res = await fetch(`/api/desk/audit-log?format=csv&${qs()}`, { headers: { Authorization: `Bearer ${localStorage.getItem('jwtToken')}` } });
            if (!res.ok) throw new Error('Could not build the CSV.');
            downloadCsv('audit-log-full', (await res.text()).replace(/^﻿/, ''));
        } catch (e) { setErr(e.message); }
    };
    return (
        <div className="space-y-4">
            <div>
                <h1 className="text-2xl font-bold text-slate-800">Audit Log</h1>
                <p className="text-sm text-slate-600">Every change made through the portal by a signed-in person. Records are not hard-deleted; a removal shows here as "removed". Request contents are not stored (they can hold passwords or personal numbers).</p>
                {err && <p className="text-red-700 text-sm">{err}</p>}
            </div>
            <div className="flex flex-wrap gap-2 items-end bg-white p-4 rounded-lg shadow-md text-sm">
                <label>From<input type="date" value={range.from} onChange={(e) => setRange({ ...range, from: e.target.value })} className="block p-2 border rounded" /></label>
                <label>To<input type="date" value={range.to} onChange={(e) => setRange({ ...range, to: e.target.value })} className="block p-2 border rounded" /></label>
                <button onClick={fullCsv} className="px-3 py-2 bg-slate-800 text-white rounded">Full CSV for these dates</button>
                <span className="text-slate-500">The list shows the latest 2,000 entries.</span>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md">
                <DataList rows={rows} rowKey="audit_id" csvName="audit-log" defaultSort={{ key: 'audit_id', dir: 'desc' }}
                    filters={[{ key: 'username', label: 'Who' }, { key: 'area', label: 'Area' }, { key: 'method', label: 'Action' }]}
                    columns={[{ key: 'audit_id', label: '#', type: 'number' }, { key: 'occurred_at', label: 'When', render: (r) => new Date(r.occurred_at).toLocaleString(), type: 'date' },
                        { key: 'username', label: 'Who' }, { key: 'role_label', label: 'Role' }, { key: 'method', label: 'Action', render: (r) => VERB[r.method] || r.method },
                        { key: 'area', label: 'Area' }, { key: 'record_id', label: 'Record' }, { key: 'path', label: 'Path' }, { key: 'ip_address', label: 'IP' }]} />
            </div>
        </div>
    );
}
